import { Stat, StatRow } from "@/components/Page.js"
import { Input, Label } from "@/components/ui/input.js"
import { Slider } from "@/components/ui/slider.js"
import type { EditorQuestion } from "./model.js"

/** The slider's range. A threshold beyond it is still typeable in the box. */
const MAX_THRESHOLD = 200

/**
 * One rule set's numbers — a question's, in a country — and the one control
 * they are all measured against.
 *
 * **`Maks. pkt` against the threshold is the figure that reads the whole
 * configuration back.** At `0 / 100` no accumulation of points can ever reach
 * the threshold, so only vetoes block — which is true of what ships today, and
 * is not visible anywhere else on the screen.
 *
 * The threshold gets the same slider-and-box as a rule's weight, because it is
 * the same kind of quantity read the same way: the box is for an exact figure,
 * the slider for where that figure sits. Giving the weights a slider and the
 * line they are measured against a bare number made the two look unrelated.
 */
export function Scoreboard({
  set,
  onChange,
}: {
  set: EditorQuestion
  onChange: (next: Partial<EditorQuestion>) => void
}) {
  const running = set.rules.filter((rule) => rule.enabled)
  const inputId = `threshold-${set.questionId}`
  const scoring = running.filter((rule) => rule.mode === "score")
  const maxPoints = scoring.reduce((sum, rule) => sum + rule.weight, 0)

  function setThreshold(value: number) {
    onChange({ blockThreshold: Math.max(Math.round(value), 1) })
  }

  return (
    <StatRow>
      <div className="w-56">
        <Label className="text-[13px] font-normal text-muted-foreground" htmlFor={inputId}>
          Próg blokady
        </Label>
        <div className="mt-1 flex items-center gap-3">
          <Input
            id={inputId}
            type="number"
            min={1}
            value={String(set.blockThreshold)}
            // Wide enough for three digits plus the number spinner: at w-16 a
            // threshold of 100 rendered as "10".
            className="h-8 w-20 px-2 text-[19px] font-semibold tabular-nums"
            onChange={(event) => {
              const parsed = Number(event.target.value)
              setThreshold(Number.isFinite(parsed) ? parsed : 100)
            }}
          />
          <Slider
            value={[Math.min(set.blockThreshold, MAX_THRESHOLD)]}
            min={1}
            max={MAX_THRESHOLD}
            step={1}
            aria-label="Suwak progu blokady"
            onValueChange={([value]) => setThreshold(value ?? 100)}
          />
        </div>
      </div>

      <Stat label="Reguły" value={running.length} />
      <Stat
        label="Weta"
        value={running.length - scoring.length}
        tone={running.length > scoring.length ? "critical" : "neutral"}
      />
      <Stat
        label="Maks. pkt"
        value={`${maxPoints} / ${set.blockThreshold}`}
        tone={maxPoints >= set.blockThreshold ? "prepay" : "neutral"}
        hint="Suma wag włączonych reguł punktujących. Poniżej progu blokują tylko weta."
      />
      <Stat label="Źródła" value={new Set(running.flatMap((rule) => rule.dependsOn)).size} />
    </StatRow>
  )
}
